 <template>
    <v-layout justify-center>
        <v-responsive max-width="1440" width="100%">
            <list-heading v-model="showSearch" title="작가 목록" />
            <v-data-table :items="items" :headers="headers" hide-default-footer disable-sort class="my-4 v-sheet--outlined" ref="data-table">
                <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" v-slot:[`item.${header.value}`]="{ value }"> {{ header.formatter(value) }} </template>

                <template #body="props">
                    <draggable v-if="$refs['data-table']" tag="tbody" :list="props.items" @change="setNewIndex" :sort="!loading">
                        <v-nodes :vnodes="$refs['data-table'].genItems(props.items, props)" />
                    </draggable>
                </template>

                <template #[`item.thumb`]="{ value }">
                    <image-popup :src="value?.url" size="53" />
                </template>

                <template #[`item.shows`]="{ item }">
                    <v-switch v-model="item.shows" class="d-inline-block ml-n2" v-bind="attrs_switch" @change="(shows) => update({ _id: item._id, shows })" />
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <author-view :value="item" @input="updateItem">
                        <template #activator="{ attrs, on }">
                            <v-btn v-bind="attrs" v-on="on" small text icon>
                                <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                    </author-view>
                    <v-btn small icon text @click="remove(item)">
                        <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                </template>
            </v-data-table>

            <v-pagination v-model="page" :length="pageCount" :total-visible="11" class="my-4" @input="search()" />
            <author-view @input="updateItem">
                <template #activator="{ attrs, on }">
                    <v-btn v-bind="attrs" v-on="on" fab fixed right bottom color="primary" large><v-icon>mdi-pencil</v-icon></v-btn>
                </template>
            </author-view>
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";
import { attrs_switch } from "@/assets/variables";

import Draggable from "vuedraggable";
import ImagePopup from "@/components/console/dumb/image-popup.vue";
import AuthorView from "@/components/console/auction/author-view.vue";
import ListHeading from "@/components/console/dumb/list-heading.vue";

const formatter_dateTime = (value) => value?.toDateTime?.() || "-";
const formatter_date = (value) => value?.toDate?.() || "-";

const headers = [
    { text: "번호", value: "index", width: 70 },
    { text: "썸네일", value: "thumb", width: 53, class: "pa-0", cellClass: "pa-0", align: "center" },
    { text: "국가(국문)", value: "country.ko" },
    { text: "국가(영문)", value: "country.en" },
    { text: "이름(국문)", value: "name.ko" },
    { text: "이름(영문)", value: "name.en" },
    { text: "생년월일", value: "bornAt", width: 110, formatter: formatter_date },
    { text: "생몰월일", value: "diedAt", width: 110, formatter: formatter_date },
    { text: "작성일", value: "createdAt", width: 160, formatter: formatter_dateTime },
    { text: "표시여부", value: "shows", width: +80, class: "pa-0", align: "center", formatter: formatter_dateTime },
    { text: "", value: "actions", width: 88, align: "right" },
].map((item, index, array) => ({ ...item, formatter: item.formatter ?? ((value) => value ?? "-"), divider: index != array.length - 1, align: item.align ?? "start", class: (item.class || "") + " white-space-pre-line", cellClass: (item.cellClass || "py-3") + " caption line-height-1-5 white-space-pre-line vertical-align-top" }));

export default {
    components: {
        VNodes: { functional: true, render: (h, ctx) => ctx.props.vnodes },

        Draggable,
        ImagePopup,
        AuthorView,
        ListHeading,
    },
    data() {
        return {
            ///////////////////////////////////////////////
            // 검색필터
            ///////////////////////////////////////////////
            showSearch: true,
            loading: false,

            filter: {
                searchKey: this.$route.query.searchKey || null,
                searchValue: this.$route.query.searchValue || null,
            },

            page: +this.$route.query.page || 1,
            totalCount: 0,
            limit: 10,

            authors: [],
            headers,
            attrs_switch,
        };
    },
    computed: {
        pageCount() {
            return Math.ceil(this.totalCount / this.limit);
        },
        items() {
            return [...(this.authors || [])];
        },
    },
    mounted() {
        this.init();
    },
    watch: {
        $route() {
            this.search(false);

            Object.assign(this.filter, this.$route.query);
            this.page = +this.$route.query.page || 1;
        },
    },
    methods: {
        init() {
            this.search(false);
        },
        manualSearch() {
            this.page = 1;
            this.search();
        },
        async search(routable = true) {
            if (routable) this.$router.push({ query: Object.assign({}, this.filter, { page: this.page }) });

            var {
                summary: { totalCount },
                authors,
            } = await api.console.auction.authors.gets({
                headers: {
                    skip: (this.page - 1) * this.limit,
                    limit: this.limit,
                    sort: JSON.stringify({ index: -1, createdAt: -1 }),
                },
                params: this.filter,
            });

            this.authors = authors;
            this.totalCount = totalCount;
        },
        async remove(item) {
            if (!confirm(`작가를 삭제하시겠습니까?\n\n작가명:    "${item?.name?.ko || ""}"\n작성일: ${item?.createdAt?.toDateTime()}`)) return;

            await api.console.auction.authors.delete(item);
            this.authors = this.authors.filter((content) => content._id !== item._id);
            alert("작가가 삭제되었습니다.");
        },
        async update(item) {
            if (this.loading) return;
            else this.loading = true;
            try {
                let { author } = await api.console.auction.authors.put(item);
                this.loading = false;
                this.updateItem(author);
            } finally {
                if (this.loading) this.loading = false;
            }
        },
        async updateItem(item) {
            const index = this.authors.findIndex(({ _id }) => _id == item._id);
            if (-1 < index) this.authors.splice(index, 1, item);
            else {
                this.authors = [item, ...this.authors];
                this.totalCount += 1;
            }
            this.search();
        },
        async setNewIndex({ moved = {} }) {
            if (this.loading) return;
            this.loading = true;

            try {
                let {
                    element: { _id, index },
                    oldIndex,
                    newIndex,
                } = moved;
                console.log({ index, oldIndex, newIndex });
                index += oldIndex - newIndex;
                console.log({ index });
                let form = { _id, index };
                let { author } = await api.console.auction.authors.put(form);

                this.loading = false;
                this.updateItem(author);
            } finally {
                if (this.loading) this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .line-height-1-5 {
        line-height: 1.5;
    }
    .max-width-0 {
        max-width: 0;
    }
    .white-space-pre-line {
        white-space: pre-line;
    }
    .cursor-pointer {
        cursor: pointer;
    }
    .v-pagination button {
        box-shadow: none !important;
        border: thin solid rgba(0, 0, 0, 0.12);
    }
    .v-small-dialog__activator {
        height: 100%;
    }
    .v-small-dialog__activator__content {
        width: 100%;
    }

    th:first-of-type:not(:last-of-type),
    td:first-of-type:not(:last-of-type) {
        border-right: thin solid rgba(0, 0, 0, 0.12);
    }

    .v-data-table__expanded__content {
        box-shadow: none !important;
    }
    .vertical-align-top {
        vertical-align: top !important;
    }
}
</style>
